import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/lib/queryClient';
import { msalInstance } from '@/features/auth/authConfig';
import { ErrorBoundary } from './ErrorBoundary';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { AccessDeniedPage } from '@/features/auth/AccessDeniedPage';
import { AppLayout } from './AppLayout';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { ImportantDatesPage } from '@/features/settings/important-dates/ImportantDatesPage';
import { UserManagementPage } from '@/features/settings/user-management/UserManagementPage';
import { GRCDashboardPage } from '@/features/grc/GRCDashboardPage';
import { DraftCalendarPage } from '@/features/draft-calendar/DraftCalendarPage';
import { DraftCalendarView } from '@/features/draft-calendar/components/DraftCalendarView';
import { MessageMonitorPage } from '@/features/message-monitor/MessageMonitorPage';

interface AppProvidersProps {
  children?: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="grcdashboard" element={<GRCDashboardPage />} />
                <Route path="draftcalendar" element={<DraftCalendarPage />} />
                <Route path="calendarscheduleview" element={<DraftCalendarView />} />
                <Route path="settings" element={<SettingsPage />}>
                  <Route index element={<Navigate to="important-dates" replace />} />
                  <Route path="important-dates" element={<ImportantDatesPage />} />
                  <Route path="user-management" element={<UserManagementPage />} />
                </Route>
                <Route path="message-monitor" element={<MessageMonitorPage />} />
              </Route>
              <Route path="access-denied" element={<AccessDeniedPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" />
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}
